import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { 
  ArrowRight, 
  Cloud, 
  Code2, 
  BarChart3, 
  ClipboardList, 
  Brain, 
  Bot,
  CheckCircle2,
  Quote
} from 'lucide-react';
import ScheduleMeetingForm from '../components/forms/ScheduleMeetingForm';

const Services = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen" data-testid="services-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 bg-slate-50" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Our Services
          </h1>
          <p className="text-lg md:text-xl text-slate-500 max-w-3xl mx-auto">
            Comprehensive digital transformation solutions to help your business thrive
          </p>
        </div>
      </section>

      {/* Cloud Services */}
      <section id="cloud" className="py-20 md:py-28 scroll-mt-24" data-testid="cloud-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <Cloud className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium tracking-widest uppercase text-blue-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  Transform your business
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Cloud Services
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                With a deep expertise in proving cloud migrations services for Azure, AWS & Google Cloud and a strong line up of Cloud Solutions Architects our implementations not only save you time, but money as well. We specialize in a range of services on the cloud from IaaS and PaaS services to leveraging best practices for improved performance and scalability with cost reductions bringing value to your organization.
              </p>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6">
                    Schedule a meeting <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[500px]">
                  <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Schedule a Meeting</DialogTitle>
                    <DialogDescription>Fill out the form and we'll get back to you within 24 hours.</DialogDescription>
                  </DialogHeader>
                  <ScheduleMeetingForm onSuccess={() => setDialogOpen(false)} />
                </DialogContent>
              </Dialog>
            </div>
            <div className="bg-slate-100 rounded-2xl p-8 aspect-video flex items-center justify-center">
              <Cloud className="w-20 h-20 text-slate-300" />
            </div>
          </div>

          {/* Go-Cloud with ZxDigital */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Go-Cloud with Zendata Solutions
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                Over the coming years, organizations will have to make decisions and consider the possibility of going to the cloud. This can happen in phases and most companies will stick to a more hybrid approach.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                It is important to ensure that you have a well defined roadmap to align with the fast moving digital world around us. Zendata Solutions can help with this process by partnering with you strategically to ensure that shift happens as planned and without hiccups.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our team of Architects and domain experts will ensure that you are in position to take advantage of the cloud and truly harness its power.
              </p>
            </div>
            <div className="space-y-3">
              {['Cloud Strategy & Consulting', 'Cloud Migration', 'Cloud Adoption', 'Data Services | Unified Analytics', 'Lift & Shift applications', 'DevOps | CI/CD', 'Application Modernization'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-slate-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Benefits of Cloud Services
            </h3>
            <blockquote className="border-l-4 border-blue-600 pl-4 mb-6 italic text-slate-600">
              "I don't need a hard disk in my computer if I can get to the server faster... carrying around these non-connected computers is byzantine by comparison" — Steve Jobs
            </blockquote>
            <p className="text-slate-600 mb-6">
              Going cloud has immense benefits and they all revolve around time & cost savings coupled with better employee and customer experience which are paramount to your enterprise Digital Strategy.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['Reduced IT Costs', 'On Demand Scaling', 'Business Continuity', 'Collaboration Efficiency', 'Flexibility of Work', 'Improved Security', 'Rapid Prototyping', 'Quick to Market'].map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* Application Development */}
      <section id="appdev" className="py-20 md:py-28 bg-slate-50 scroll-mt-24" data-testid="appdev-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <Code2 className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium tracking-widest uppercase text-blue-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  Enrich your client experience
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Application Development
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                As your organization grows, our team helps with designing and modernizing your applications using microservices architecture. We work with you as a part of your organization and help you arrive at a final product using state-of-the art technology and practices. We also provide detailed application and maintenance support.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 aspect-video flex items-center justify-center border border-slate-200">
              <Code2 className="w-20 h-20 text-slate-300" />
            </div>
          </div>

          {/* Build Modern Apps */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                Build Modern Apps
              </h3>
              <p className="text-blue-600 font-medium mb-4">Don't settle for the subpar</p>
              <p className="text-slate-600 leading-relaxed mb-4">
                We think of ourselves as your strategic partner and not just an independent contractor. We work alongside you and dwell in the innovative spark. The products that we create together are sleek keeping the user experience at paramount.
              </p>
              <p className="text-slate-600 leading-relaxed">
                Our focus is to get the products to the market as quickly as possibly and our team of UI/UX engineers, Developers and Scrum Masters ensure that a Six Sigma quality is maintained.
              </p>
            </div>
            <div className="space-y-3">
              {['Enterprise Applications & solutions', 'IOT', 'UI-UX Design', 'Android', 'IOS', 'Analytics'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Three Pillars */}
          <div className="bg-white rounded-2xl p-8 border border-slate-200">
            <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              The Three Pillars of Application Development
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-slate-50 rounded-xl">
                <h4 className="font-bold text-slate-900 mb-2">User Experience</h4>
                <p className="text-sm text-slate-600">Keep the client/customer experience in the spotlight as this is the secret sauce to success.</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-xl">
                <h4 className="font-bold text-slate-900 mb-2">Adaptability</h4>
                <p className="text-sm text-slate-600">Build applications with a mindset for future adaptability and easy feature additions.</p>
              </div>
              <div className="text-center p-6 bg-slate-50 rounded-xl">
                <h4 className="font-bold text-slate-900 mb-2">Security</h4>
                <p className="text-sm text-slate-600">Our security experts ensure the app passes through best-in-class checks and balances.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* Data & Analytics */}
      <section id="data" className="py-20 md:py-28 scroll-mt-24" data-testid="data-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium tracking-widest uppercase text-blue-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  Generate meaningful insights
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Data & Analytics
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Data being the new oil of the world. We have deep expertise in generating insights and devising your analytical framework. Our solid bench has qualified architects with expertise in data warehousing, data lakes, big data workloads and file systems batch and real time processing, Dashboards, and applied intelligence.
              </p>
              <p className="text-slate-500 text-sm">
                Technologies: Azure Synapse, Databricks, Apache Kafka, Azure Data Factory, Azure IOT Hub, Event Hubs, Azure Data Lake Gen2, Power BI Embed, Tableau, Talend
              </p>
            </div>
            <div className="bg-slate-100 rounded-2xl p-8 aspect-video flex items-center justify-center">
              <BarChart3 className="w-20 h-20 text-slate-300" />
            </div>
          </div>

          {/* Our Expertise */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 mb-16">
            <blockquote className="text-2xl font-medium mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              "Without data you're just a person with an opinion"
            </blockquote>
            <cite className="text-slate-400">— W. Edwards Demming</cite>
            <p className="text-slate-300 mt-6 leading-relaxed">
              All organizations sit on a gold mine of data and information. This information can reveal incredible insights and bring value for your business. We at Zendata Solutions have the experience and the know-how of generating these insights in a repeatable way by enabling technology that can be accessed by all.
            </p>
          </div>

          {/* Data Strategy & Governance */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Data Strategy & Governance
              </h3>
              <blockquote className="border-l-4 border-blue-600 pl-4 mb-6 italic text-slate-600">
                "It is a capital mistake to theorize before one has data." — Sherlock Holmes
              </blockquote>
              <p className="text-slate-600 leading-relaxed">
                It is vital that organizations have a full fledge road map to where they see themselves being data ready and data savvy. Data as a strategy can drive immense value to a company as this means you bring the "T" in data. "T" implying trust.
              </p>
            </div>
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-900 mb-4">Blueprint to solid Governance</h4>
              {['Stakeholders & Domain Identification', 'Define Operational Model', 'Governance Workflows & KPIs', 'Data quality Dashboards', 'Incentivise Compliance', 'Engage Senior Management', 'Compliance to Laws & Regulations'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* Project Management */}
      <section id="pm" className="py-20 md:py-28 bg-slate-50 scroll-mt-24" data-testid="pm-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <ClipboardList className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium tracking-widest uppercase text-blue-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  No I in Team
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Project Management
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                At Zendata Solutions our talented bench of professionals can help you mitigate risks and ensure all parts of a project are in motion breaking down barriers with effective communication and collaborative practices. We passionately believe that there is "No I in Team" and over the years we have employed a cyclical approach towards collaboration helping organizations to the desired finish lines.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 aspect-video flex items-center justify-center border border-slate-200">
              <ClipboardList className="w-20 h-20 text-slate-300" />
            </div>
          </div>

          {/* Quote */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 mb-16">
            <blockquote className="text-2xl font-medium mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              "Those who plan do better than those who do not plan, even though they rarely stick to their plan."
            </blockquote>
            <cite className="text-slate-400">— Winston Churchill</cite>
          </div>

          {/* Agile & Waterfall */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <Card className="p-6 bg-white border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>The Agile</h3>
              <p className="text-blue-600 text-sm font-medium mb-4">Iterative Approach</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Agile methodology is a practice that supports continuous iterations of work in the SDLC process. In this model, development and testing activities are concurrent. At ZxDigital our scrum masters are equipped with the industry know-how to make this approach work for you.
              </p>
            </Card>
            <Card className="p-6 bg-white border border-slate-200">
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>The Waterfall</h3>
              <p className="text-blue-600 text-sm font-medium mb-4">Linear Sequential</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Waterfall methodology follows a sequential order of doing tasks and project development can only move to the next phase if the previous step is completed. At ZxDigital our experienced PM's have the knowledge to keep the project moving.
              </p>
            </Card>
          </div>

          {/* Outcomes */}
          <div className="space-y-3">
            <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              What You Get
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {['Optimized plans', 'Manageable schedule', 'Appropriate controls', 'On time delivery', 'Cost reductions', 'Reduced risks', 'Effective communication'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-white rounded-lg border border-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* AI & Machine Learning */}
      <section id="ai" className="py-20 md:py-28 scroll-mt-24" data-testid="ai-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <Brain className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium tracking-widest uppercase text-blue-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  Shift from hindsight to foresight
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                AI & Machine Learning
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our Deep learning & Machine Learning engineers have an array of experience from building Image Classifiers to applying natural language processing techniques that drive foresights and value. We have built several predictive models around lead scoring, churn predictions, segmentations that align well organizational goals.
              </p>
            </div>
            <div className="bg-slate-100 rounded-2xl p-8 aspect-video flex items-center justify-center">
              <Brain className="w-20 h-20 text-slate-300" />
            </div>
          </div>

          {/* Quote */}
          <div className="bg-blue-600 text-white rounded-2xl p-8 mb-16">
            <blockquote className="text-2xl font-medium mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              "The value of an idea lies in the using of it."
            </blockquote>
            <cite className="text-blue-200">— Thomas Edison</cite>
          </div>

          {/* Expertise */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                Our Expertise
              </h3>
              <p className="text-slate-600 leading-relaxed">
                We help organizations thrive and accelerate AI & Machine Learning services from the ground up. Our team ensures you have the best Applied solution. We enable your team by empowering them to work smart with AI by setting up literacy workshops across your organization.
              </p>
            </div>
            <div className="space-y-3">
              {['Text Extraction & Analysis', 'Object detection', 'Predictive & Prescriptive Modelling', 'Time Series Forecasting', 'Anomaly Detection'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Why Work With Us */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 bg-slate-50 border-0">
              <h4 className="font-bold text-slate-900 mb-2">Domain Expertise</h4>
              <p className="text-sm text-slate-600">Our team has worked across multiple domains solving different business problems.</p>
            </Card>
            <Card className="p-6 bg-slate-50 border-0">
              <h4 className="font-bold text-slate-900 mb-2">Human-Centric</h4>
              <p className="text-sm text-slate-600">We believe humans make decisions and machines provide insights.</p>
            </Card>
            <Card className="p-6 bg-slate-50 border-0">
              <h4 className="font-bold text-slate-900 mb-2">Technology & Methods</h4>
              <p className="text-sm text-slate-600">Our knowledge empowers us with best practices on what works.</p>
            </Card>
            <Card className="p-6 bg-slate-50 border-0">
              <h4 className="font-bold text-slate-900 mb-2">Technical Depth</h4>
              <p className="text-sm text-slate-600">Expertise in Computer Vision, NLP, and predictive modelling.</p>
            </Card>
          </div>
        </div>
      </section>

      <div className="border-t border-slate-100" />

      {/* RPA */}
      <section id="rpa" className="py-20 md:py-28 bg-slate-50 scroll-mt-24" data-testid="rpa-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
                  <Bot className="w-6 h-6" />
                </div>
                <p className="text-sm font-medium tracking-widest uppercase text-blue-600" style={{ fontFamily: 'var(--font-heading)' }}>
                  Shift from manual to automation
                </p>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Robotic Process Automation
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                Our RPA engineers specialize in automating your day to day business processes in a controlled manner saving you time and driving operational efficiency. Our expertise spans across data entry automation, Desktop automation, GUI automation, Web Scraping, Screen Scraping, Excel Automation, Extraction algorithms and connecting your business applications with RPA.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8 aspect-video flex items-center justify-center border border-slate-200">
              <Bot className="w-20 h-20 text-slate-300" />
            </div>
          </div>

          {/* Quote */}
          <div className="bg-slate-900 text-white rounded-2xl p-8 mb-16">
            <blockquote className="text-2xl font-medium mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              "Automation is great, so long as you know where exactly to put the machine"
            </blockquote>
            <cite className="text-slate-400">— Bill Gates</cite>
          </div>

          {/* Expertise */}
          <div className="space-y-3 mb-16">
            <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              Our Expertise
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {['Automating data entry tasks', 'Extract data from forms using Deep Learning', 'Building end to end pipelines', 'Desktop Automation', 'Excel Automation', 'Screen Scraping Automation', 'Web Scraping Automation', 'Holistic & Connected Automation'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tools */}
          <p className="text-slate-500 text-sm text-center">
            We work with UIPATH, Blue Prism, Automation Anywhere, and other leading RPA platforms.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-slate-900" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
            Ready to Transform Your Business?
          </h2>
          <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you leverage technology for competitive advantage.
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-10 py-6 text-base">
                Let's Talk <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'var(--font-heading)' }}>Schedule a Meeting</DialogTitle>
                <DialogDescription>Fill out the form and we'll get back to you within 24 hours.</DialogDescription>
              </DialogHeader>
              <ScheduleMeetingForm />
            </DialogContent>
          </Dialog>
        </div>
      </section>
    </div>
  );
};

export default Services;
